import { useAuth0 } from "@auth0/auth0-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Edit } from "@mui/icons-material";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import PageHeader from "@/components/PageHeader";
import { useOwnerEditUnitMutation } from "@/mutations/unit.ts";
import { useQueryOptionsFactory } from "@/queries";
import type { Owner, OwnerUnit } from "@/queries/owners.ts";

export const Route = createFileRoute("/owner/units/")({
    component: RouteComponent,
});

function RouteComponent() {
    const { user } = useAuth0();

    if (!user?.email) {
        throw new Error("Auth0 user with no email.");
    }

    const qof = useQueryOptionsFactory();
    const pageData: { data: Owner } = useSuspenseQuery(qof.owners.get(user?.email));

    return <DisplayComponent owner={pageData.data} />;
}

const instructions = [
    "View and manage all units you own",
    "Edit unit-specific contact information",
    "Update unit phone and on-island agent details",
];

const DisplayComponent = ({ owner }: { owner: Owner }) => {
    const [selectedUnit, setSelectedUnit] = useState<OwnerUnit | null>(null);

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader title={"My Units"} instructions={instructions} />

                <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            color: "#1e293b",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        Unit Number
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            color: "#1e293b",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        Unit Phone
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            color: "#1e293b",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        On-Island Agent
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            color: "#1e293b",
                                            fontSize: "0.875rem",
                                            textAlign: "center",
                                        }}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {owner.calendars.map((unit) => (
                                    <TableRow
                                        key={`unit-${unit.rentalId}`}
                                        sx={{
                                            "&:hover": {
                                                backgroundColor: "#f8fafc",
                                            },
                                            transition: "background-color 0.2s ease",
                                        }}
                                    >
                                        <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>
                                            {unit.rentalId}
                                        </TableCell>
                                        <TableCell sx={{ color: "#64748b" }}>
                                            {unit.unitPhone || "-"}
                                        </TableCell>
                                        <TableCell sx={{ color: "#64748b" }}>
                                            {unit.rentalAgentPhone || "-"}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "center" }}>
                                            <IconButton
                                                onClick={() => setSelectedUnit(unit)}
                                                sx={{
                                                    color: "#3b82f6",
                                                    "&:hover": {
                                                        backgroundColor: "#eff6ff",
                                                    },
                                                }}
                                            >
                                                <Edit />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                {selectedUnit !== null && (
                    <EditUnitForm unit={selectedUnit} close={() => setSelectedUnit(null)} />
                )}
            </Paper>
        </Box>
    );
};

const unitEditSchema = z.object({
    unitPhone: z.string().optional(),
    rentalAgentPhone: z.string().optional(),
});

type UnitEditFormData = z.infer<typeof unitEditSchema>;

const inputStyles = {
    "& .MuiOutlinedInput-root": {
        backgroundColor: "#f8fafc",
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#3b82f6",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#3b82f6",
        },
    },
};

type EditFormDataProps = {
    unit: OwnerUnit;
    close: () => void;
};
const EditUnitForm = ({ unit, close }: EditFormDataProps) => {
    const form = useForm<UnitEditFormData>({
        resolver: zodResolver(unitEditSchema),
        defaultValues: {
            unitPhone: unit.unitPhone,
            rentalAgentPhone: unit.rentalAgentPhone,
        },
    });
    const editUnitMutation = useOwnerEditUnitMutation(`${unit.id}`);

    useEffect(() => {
        if (editUnitMutation.isSuccess) {
            close();
        }
    }, [editUnitMutation.isSuccess, close]);

    const handleSubmit = (data: UnitEditFormData) => {
        editUnitMutation.mutate({
            attributes: data,
            type: "calendar",
        });
    };

    return (
        <Dialog open={true} onClose={close} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    fontWeight: 600,
                    color: "#1e293b",
                    borderBottom: "1px solid #e2e8f0",
                    pb: 2,
                }}
            >
                Edit Unit {unit.rentalId}
            </DialogTitle>
            <DialogContent sx={{ pt: 4, px: 3 }}>
                <form id="unit-edit-form" onSubmit={form.handleSubmit(handleSubmit)}>
                    <Grid container spacing={3}>
                        <Grid size={12} sx={{ pt: 2 }}>
                            <Controller
                                name="unitPhone"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Unit Phone"
                                        variant="outlined"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        sx={inputStyles}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={12}>
                            <Controller
                                name="rentalAgentPhone"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="On-Island Agent"
                                        variant="outlined"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        sx={inputStyles}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 3,
                    pt: 2,
                    borderTop: "1px solid #e2e8f0",
                }}
            >
                <Button
                    onClick={close}
                    sx={{
                        color: "#64748b",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                            backgroundColor: "#f8fafc",
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    form="unit-edit-form"
                    variant="contained"
                    sx={{
                        backgroundColor: "#22c55e",
                        color: "white",
                        fontWeight: 600,
                        textTransform: "none",
                        px: 3,
                        "&:hover": {
                            backgroundColor: "#16a34a",
                        },
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};
